/**
 * Pódio ilustrado + lista completa — o desenho do Top Comprador, reutilizado
 * para qualquer título ou medalha (PO, 20/09/2026: "todas as categorias
 * clicáveis, em destaque, cada uma com o contador do seu estilo").
 *
 * O que muda entre um e outro é só `valor(n)`: dinheiro no Top Comprador e
 * nas medalhas por tipo de gasto, "16 atividades" / "3 fotos" nos títulos.
 * O selo "Top comprador" é asset do dinheiro; os selos "Top xxx" dos títulos
 * o PO cria depois — até lá, o cabeçalho da folha faz esse papel.
 */
import { useTrip } from '../../trip/TripContext';
import { FotoAvatar } from '../perfil/FotoAvatar';

export interface Linha {
  memberId: string;
  name: string;
  value: number;
  position: number;
}

/** No pódio cabe só o primeiro nome; a lista tem o nome inteiro. */
export const primeiroNome = (nome: string) => nome.trim().split(/\s+/)[0] ?? nome;

export function Medalha({ pos }: { pos: number }) {
  return <span className={`rk__medalha rk__medalha--${pos}`}>{pos}</span>;
}

export function Classificacao({
  linhas,
  valor,
  meuMemberId,
  selo,
  apenasDepoisDoPodio = false,
  testId = 'rk',
}: {
  /** Já ordenadas, com posição (empate divide). */
  linhas: Linha[];
  /** Como o número é dito: dinheiro ou "N unidades". */
  valor: (n: number) => string;
  meuMemberId: string;
  /** Etiqueta no alto do pódio (o Top Comprador; refino final: em código). */
  selo?: React.ReactNode;
  /** No card principal a lista começa no 4º: o pódio já mostra os três. */
  apenasDepoisDoPodio?: boolean;
  testId?: string;
}) {
  const { members } = useTrip();
  const corDe = (memberId: string) => members.find((m) => m.memberId === memberId)?.familyColor;
  const fotoDe = (memberId: string) => members.find((m) => m.memberId === memberId)?.avatarUrl;
  const familiaDe = (memberId: string) => members.find((m) => m.memberId === memberId)?.familyName;

  // O pódio mostra as três primeiras LINHAS (não posições): num empate em 1º
  // as duas sobem, e a terceira coluna fica com quem vem depois.
  const podio = linhas.slice(0, 3).map((l, i) => ({ ...l, coluna: i + 1 }));
  const colunas = [podio[1], podio[0], podio[2]];

  return (
    <>
      {selo && <div className="rk__selo">{selo}</div>}
      {podio.length > 0 && (
        <div className="rk__podio" data-testid={`${testId}-podio`}>
          <img className="rk__podio-fundo" src="/ilustracoes/podium.png" alt="" width={400} height={184} aria-hidden="true" />
          {colunas.map((p, i) =>
            p ? (
              <div className={`rk__coluna rk__coluna--${p.coluna}`} key={p.memberId} data-testid={`${testId}-podio-${p.coluna}`}>
                {p.coluna === 1 && <img className="rk__coroa nv-float" src="/ilustracoes/crown-rank.png" alt="" width={100} height={99} aria-hidden="true" />}
                <span className="rk__coluna-avatar">
                  <FotoAvatar nome={p.name} url={fotoDe(p.memberId)} cor={corDe(p.memberId)} tamanho={p.coluna === 1 ? 72 : 56} />
                  <Medalha pos={p.position} />
                </span>
                <span className="rk__coluna-nome">
                  {primeiroNome(p.name)}
                  {p.memberId === meuMemberId && <span className="rk__eu"> (você)</span>}
                </span>
                <span className="rk__coluna-total">{valor(p.value)}</span>
              </div>
            ) : (
              <div className={`rk__coluna rk__coluna--${[2, 1, 3][i]}`} key={`vazio-${i}`} />
            ),
          )}
        </div>
      )}
      <ol className="rk__lista">
        {(apenasDepoisDoPodio ? linhas.slice(3) : linhas).map((p) => (
          <li className={p.position === 1 ? 'rk__linha rk__linha--lider' : 'rk__linha'} key={p.memberId} data-testid={`${testId}-linha`}>
            <span className="rk__pos mono">{p.position}º</span>
            <FotoAvatar nome={p.name} url={fotoDe(p.memberId)} cor={corDe(p.memberId)} tamanho={34} />
            <span className="rk__linha-texto">
              <span className="rk__linha-nome">
                {p.name}
                {p.memberId === meuMemberId && <span className="rk__eu"> (você)</span>}
              </span>
              {familiaDe(p.memberId) && <span className="rk__linha-familia">família {familiaDe(p.memberId)}</span>}
            </span>
            <span className="rk__linha-total mono">{valor(p.value)}</span>
          </li>
        ))}
      </ol>
      {linhas.length === 0 && <p className="enq__vazio">Ninguém está no ranking agora.</p>}
    </>
  );
}
